// The one list row: tap opens it, double-tap runs a quick action, long-press / right-click / the
// menu key / the "more" button open the action menu, and a swipe runs the row's start or end
// action. All of them are driven by the same `actions` list, so none is gesture-only.
import { useState } from "react";
import { useT } from "../lib/i18n";
import { ActionSheet } from "./ActionSheet";
import { IconButton } from "./controls";
import type { RowAction } from "./gestures/actions";
import { GestureArea } from "./gestures/GestureArea";
import { Icon } from "./Icon";
import { Fill, Row } from "./layout";
import { SwipeRow } from "./SwipeRow";
import { Body, Caption } from "./text";

/** The least width a row's text keeps before its controls wrap onto a line of their own. */
const TEXT_MIN = 180;

export interface ListRowProps {
  title: string;
  subtitle?: string | undefined;
  meta?: string | undefined;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  /**
   * Buttons that live beside the row's press target instead of inside it, so the row's accessible
   * name stays its own text and no button is nested in another. They wrap under the text when the
   * row is narrow, rather than squeezing it.
   */
  controls?: React.ReactNode;
  onPress?: (() => void) | undefined;
  /** The quick action: double-tap (also reachable from `actions`). */
  onDoubleTap?: (() => void) | undefined;
  /** Everything one can do to this row: the long-press menu and the "more" button list them all. */
  actions?: readonly RowAction[] | undefined;
  /** Dragging right runs this; it should also be in `actions`. */
  swipeStart?: RowAction | undefined;
  /** Dragging left runs this; it should also be in `actions`. */
  swipeEnd?: RowAction | undefined;
  selected?: boolean | undefined;
  testId?: string | undefined;
  /** Overrides the accessible name when the visible title alone is not enough ("Open Barolo"). */
  ariaLabel?: string | undefined;
}

export function ListRow({
  title,
  subtitle,
  meta,
  leading,
  trailing,
  controls,
  onPress,
  onDoubleTap,
  actions = [],
  swipeStart,
  swipeEnd,
  selected = false,
  testId,
  ariaLabel,
}: ListRowProps): React.JSX.Element {
  const { t } = useT();
  const [menu, setMenu] = useState(false);
  const hasMenu = actions.length > 0;
  return (
    <>
      <SwipeRow start={swipeStart} end={swipeEnd}>
        <Row
          gap="3"
          wrap={controls !== undefined}
          paddingVertical="3"
          paddingHorizontal="4"
          minHeight="var(--uiTarget)"
          backgroundColor={selected ? "accentSoft" : "surface"}
          borderBottomWidth="var(--borderW)"
          borderBottomColor="line"
          // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
          {...(testId === undefined ? {} : { "data-testid": testId })}
        >
          <GestureArea
            flex={1}
            minWidth={controls === undefined ? 0 : TEXT_MIN}
            flexDirection="row"
            alignItems="center"
            gap="3"
            // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
            {...(onPress === undefined
              ? {}
              : ({ role: "button", tabIndex: 0, cursor: "pointer" } as const))}
            // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
            {...(ariaLabel === undefined ? {} : { "aria-label": ariaLabel })}
            onKeyDown={(event: { key: string; preventDefault: () => void }) => {
              if (onPress === undefined || (event.key !== "Enter" && event.key !== " ")) return;
              event.preventDefault();
              onPress();
            }}
            onTap={onPress}
            onDoubleTap={onDoubleTap}
            onLongPress={hasMenu ? () => setMenu(true) : undefined}
            axis="x"
          >
            {leading}
            <Fill>
              <Body weight="medium" truncate>
                {title}
              </Body>
              {subtitle !== undefined && <Caption>{subtitle}</Caption>}
            </Fill>
            {meta !== undefined && <Caption>{meta}</Caption>}
            {trailing}
          </GestureArea>
          {controls !== undefined && (
            <Row gap="2" marginLeft="auto" flexShrink={0}>
              {controls}
            </Row>
          )}
          {hasMenu && (
            <IconButton
              look="ghost"
              aria-label={t("common.action.more")}
              aria-haspopup="dialog"
              onPress={() => setMenu(true)}
            >
              <Icon name="list" size={18} />
            </IconButton>
          )}
        </Row>
      </SwipeRow>
      {menu && <ActionSheet label={title} actions={actions} onClose={() => setMenu(false)} />}
    </>
  );
}
